import type { ReactNode } from "react";
import { Icon } from "./Icon";
import "./auth.css";

/**
 * DESIGN_SYSTEM.md §7 — the login treatment.
 *
 * A two-panel split: a brand panel carrying the accent gradient, and the form
 * on a plain ground. The earlier centred-card version was honest but read as
 * unfinished — this is the first screen a CCO sees, and for a product sold on
 * a risk budget it has to look like something worth $120k a year.
 *
 * The gradient stays in Echo's own indigo (`--color-accent` → `--color-accent-
 * active`) rather than borrowing Radar's magenta. They are sibling products
 * with deliberately separate identities, and a shared login palette would
 * suggest a shared platform that does not exist (ARCHITECTURE.md §1.3).
 *
 * Below tablet the brand panel is removed rather than stacked. On a phone it
 * would push the form below the fold, and this screen's job is unchanged from
 * the spec: "get a returning user into their dashboard in one glance."
 */

const POINTS = [
  {
    icon: "search" as const,
    text: "See what ChatGPT, Perplexity, Gemini, Copilot and Google AI Mode say about you",
  },
  {
    icon: "drift" as const,
    text: "Measure how far that has drifted from your own official positioning",
  },
  {
    icon: "actions" as const,
    text: "Trace every discrepancy to its source, and prove the fix worked",
  },
];

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="auth">
      <aside className="auth__brandpanel" aria-hidden="true">
        <div className="auth__brandhead">
          <span className="auth__mark">
            <Icon name="echo" size={24} />
          </span>
          <span className="auth__wordmark">Echo</span>
        </div>

        <div className="auth__pitch">
          <span className="auth__eyebrow">
            AI Reputation &amp; Narrative Intelligence
          </span>
          <h2 className="auth__headline">
            What AI engines tell the world about your company
          </h2>
          <ul className="auth__points">
            {POINTS.map((p) => (
              <li key={p.text}>
                <span className="auth__pointicon">
                  <Icon name={p.icon} size={16} />
                </span>
                <span>{p.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <span className="auth__legal">Echo by Velivo</span>
      </aside>

      <section className="auth__panel">
        <div className="auth__card">
          <div className="auth__cardbrand">
            <span className="auth__mark auth__mark--sm">
              <Icon name="echo" size={16} />
            </span>
            <span className="auth__wordmark auth__wordmark--sm">Echo</span>
          </div>
          <h1 className="auth__title">{title}</h1>
          {subtitle && <p className="auth__subtitle">{subtitle}</p>}
          {children}
          {footer && <div className="auth__footer">{footer}</div>}
        </div>
      </section>
    </main>
  );
}
